/**
 * Behaviour shared by the Quote First, Editorial and Tours & Destinations themes.
 * Each piece only runs when its markup is on the page, so one small module serves
 * every public page. Loaded as a module: it runs before DOMContentLoaded.
 */
import { preventDoubleSubmit } from '../../../js/submit-once';
import { initQuoteWizard } from './quote-wizard';
import { initVehicleGallery } from './vehicle-gallery';
import { initFaq } from './faq';
import { initGalleryPage } from './gallery-page';
import { initTripPlanner } from './trip-planner';

const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

function initMobileNav() {
    const toggle = document.querySelector('[data-nav-open]');
    const panel = document.getElementById('ui-mobile-panel');
    if (!toggle || !panel) {
        return;
    }

    const setOpen = (open) => {
        panel.classList.toggle('is-open', open);
        toggle.setAttribute('aria-expanded', String(open));
        document.body.classList.toggle('ui-no-scroll', open);
        if (open) {
            panel.querySelector('a, button')?.focus();
        }
    };

    toggle.addEventListener('click', () => setOpen(true));
    panel.querySelectorAll('[data-nav-close]').forEach((button) => button.addEventListener('click', () => {
        setOpen(false);
        toggle.focus();
    }));
    document.addEventListener('keydown', (event) => {
        if (event.key === 'Escape' && panel.classList.contains('is-open')) {
            setOpen(false);
            toggle.focus();
        }
    });
}

/** <details> dropdowns (language, sub-menus) close on outside click and Escape. */
function initDropdowns() {
    const dropdowns = [...document.querySelectorAll('details[data-dropdown]')];
    if (!dropdowns.length) {
        return;
    }
    document.addEventListener('click', (event) => {
        dropdowns.forEach((details) => {
            if (details.open && !details.contains(event.target)) {
                details.open = false;
            }
        });
    });
    document.addEventListener('keydown', (event) => {
        if (event.key === 'Escape') {
            dropdowns.forEach((details) => { details.open = false; });
        }
    });
}

function initHeaderShadow() {
    const header = document.querySelector('.ui-header--sticky');
    if (!header) {
        return;
    }
    const update = () => header.classList.toggle('is-scrolled', window.scrollY > 8);
    update();
    window.addEventListener('scroll', update, { passive: true });
}

function initToTop() {
    const button = document.getElementById('ui-to-top');
    if (!button) {
        return;
    }
    const update = () => button.classList.toggle('is-visible', window.scrollY > 600);
    update();
    window.addEventListener('scroll', update, { passive: true });
    button.addEventListener('click', (event) => {
        event.preventDefault();
        window.scrollTo({ top: 0, behavior: reducedMotion.matches ? 'auto' : 'smooth' });
    });
}

/** Sections fade in as they scroll into view; anything already visible shows at once. */
function initReveal() {
    const boxes = [...document.querySelectorAll('.ui-reveal')];
    if (!boxes.length) {
        return;
    }
    if (reducedMotion.matches || !('IntersectionObserver' in window)) {
        boxes.forEach((box) => box.classList.add('is-in'));
        return;
    }
    const observer = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
            if (entry.isIntersecting) {
                entry.target.classList.add('is-in');
                observer.unobserve(entry.target);
            }
        });
    }, { rootMargin: '0px 0px -8% 0px' });
    boxes.forEach((box) => observer.observe(box));
}

/** Flash messages that ask for it close themselves after a few seconds. */
function initAutoDismiss() {
    document.querySelectorAll('[data-autodismiss]').forEach((alert) => {
        window.setTimeout(() => alert.remove(), Number(alert.dataset.autodismiss) || 6000);
    });
}

/** Date-time pickers for any field marked data-datetime (flatpickr is loaded on those pages). */
export function initDateTimePickers(root = document) {
    if (typeof window.flatpickr !== 'function') {
        return;
    }
    root.querySelectorAll('input[data-datetime]:not(.flatpickr-input)').forEach((input) => {
        window.flatpickr(input, {
            enableTime: input.dataset.datetime !== 'date',
            dateFormat: input.dataset.datetime === 'date' ? 'd/m/Y' : 'd/m/Y H:i',
            time_24hr: true,
            minDate: 'today',
            disableMobile: true,
            locale: { firstDayOfWeek: 1 },
        });
    });
}

function initYear() {
    document.querySelectorAll('[data-current-year]').forEach((el) => { el.textContent = String(new Date().getFullYear()); });
}

document.documentElement.classList.remove('no-js');
preventDoubleSubmit();
initMobileNav();
initDropdowns();
initHeaderShadow();
initToTop();
initReveal();
initAutoDismiss();
initYear();

document.addEventListener('DOMContentLoaded', () => {
    initDateTimePickers();
    initTripPlanner();
    initQuoteWizard();
    initVehicleGallery();
    initFaq();
    initGalleryPage();
});

window.BusRent = { initDateTimePickers };
